import { useEffect, useMemo, useState } from "react";
import { Marked } from "marked";
import { BRAND } from "../../mock";
import Footer from "./Footer";

const SITIO = "https://soypacoia.com";

/** "3. Datos que recabamos" -> "3-datos-que-recabamos" */
function slug(texto) {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Markdown -> HTML. El contenido son los .md del propio repo
// (src/content/legal), nunca texto de usuarios: por eso se puede inyectar.
// `breaks` respeta los saltos de linea simples (bloque de firma y fecha).
const md = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    heading(text, level, raw) {
      const id = level === 2 ? ` id="${slug(raw)}"` : "";
      return `<h${level}${id}>${text}</h${level}>\n`;
    },
    // Las tablas anchas hacen scroll dentro de su caja, no en la pagina.
    table(header, body) {
      return `<div class="legal-tabla"><table><thead>${header}</thead><tbody>${body}</tbody></table></div>\n`;
    },
  },
});

function useDocumento(ruta, titulo, descripcion) {
  useEffect(() => {
    const tituloPrevio = document.title;
    document.title = titulo;

    // index.html trae canonical y description de la home: en estas paginas
    // deben apuntar a la propia URL para que se indexen como paginas aparte.
    const restaurar = [];
    const poner = (selector, atributo, valor) => {
      const el = document.querySelector(selector);
      if (!el) return;
      const previo = el.getAttribute(atributo);
      el.setAttribute(atributo, valor);
      restaurar.push(() => el.setAttribute(atributo, previo ?? ""));
    };
    poner('link[rel="canonical"]', "href", `${SITIO}${ruta}`);
    poner('meta[name="description"]', "content", descripcion);

    return () => {
      document.title = tituloPrevio;
      restaurar.forEach((fn) => fn());
    };
  }, [ruta, titulo, descripcion]);
}

/**
 * Pagina legal: renderiza un Markdown con el estilo del sitio.
 * `fuente` es la URL del .md (import de webpack: va con hash, asi un cambio
 * de texto nunca queda atrapado en la cache de /static/).
 */
export default function LegalPage({ fuente, ruta, titulo, descripcion, otra }) {
  const [texto, setTexto] = useState(null);
  const [fallo, setFallo] = useState(false);

  useDocumento(ruta, titulo, descripcion);

  useEffect(() => {
    let vivo = true;
    setTexto(null);
    setFallo(false);
    fetch(fuente)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => vivo && setTexto(t))
      .catch(() => vivo && setFallo(true));
    return () => {
      vivo = false;
    };
  }, [fuente]);

  // El indice va entre el encabezado (titulo, fecha, intro) y la primera
  // seccion: el documento se parte en el primer "## ".
  const { intro, secciones, indice } = useMemo(() => {
    if (!texto) return { intro: "", secciones: "", indice: [] };
    const corte = texto.search(/^## /m);
    return {
      intro: md.parse(corte < 0 ? texto : texto.slice(0, corte)),
      secciones: corte < 0 ? "" : md.parse(texto.slice(corte)),
      indice: md
        .lexer(texto)
        .filter((t) => t.type === "heading" && t.depth === 2)
        .map((t) => ({ id: slug(t.text), texto: t.text })),
    };
  }, [texto]);

  // Al entrar: arriba, o en la seccion del #hash si viene una.
  useEffect(() => {
    if (!intro) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const destino = id && document.getElementById(id);
    if (destino) destino.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [intro]);

  return (
    <div className="legal">
      <header className="legal-barra">
        <a href="/" className="legal-marca">
          {BRAND.channel}
        </a>
        <a href="/" className="legal-volver">
          ← Inicio
        </a>
      </header>

      <main className="legal-cuerpo">
        {fallo && (
          <p role="alert">
            No pudimos cargar este documento. Recarga la página o escríbenos a{" "}
            <a href="mailto:soypacoia@gmail.com">soypacoia@gmail.com</a>.
          </p>
        )}
        {!fallo && !intro && <p className="legal-cargando">Cargando…</p>}

        {intro && (
          <>
            {/* Contenido propio del repo (ver arriba). */}
            <div className="legal-texto" dangerouslySetInnerHTML={{ __html: intro }} />
            {indice.length > 0 && (
              <nav className="legal-indice" aria-label="Contenido">
                <div className="legal-indice-titulo">Contenido</div>
                <ul>
                  {indice.map((s) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`}>{s.texto}</a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            <div
              className="legal-texto"
              dangerouslySetInnerHTML={{ __html: secciones }}
            />
            <p className="legal-otra">
              Consulta también: <a href={otra.ruta}>{otra.texto}</a>
            </p>
          </>
        )}
      </main>

      <Footer />

      <style>{`
        .legal {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--surface-light);
          color: var(--ink);
          font-family: "DM Sans", system-ui, sans-serif;
        }
        .legal-barra {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          width: 100%;
          max-width: 720px;
          margin: 0 auto;
          padding: 24px 24px 0;
        }
        .legal-marca {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--ink);
        }
        .legal-volver {
          font-size: 13px;
          color: var(--ink-dim);
          transition: color 0.2s ease;
        }
        .legal-volver:hover { color: var(--ink); }

        .legal-cuerpo {
          flex: 1;
          width: 100%;
          max-width: 720px;
          margin: 0 auto;
          padding: 40px 24px 72px;
          font-size: 17px;
          line-height: 1.7;
          color: rgba(17, 17, 16, 0.84);
          overflow-wrap: break-word;
        }
        .legal-cargando { color: var(--ink-dim); }

        .legal-texto h1 {
          font-family: "Playfair Display", serif;
          font-size: clamp(2.1rem, 6vw, 3rem);
          font-weight: 500;
          letter-spacing: -0.02em;
          line-height: 1.08;
          color: var(--ink);
          margin: 0 0 18px;
        }
        .legal-texto h2 {
          font-family: "Playfair Display", serif;
          font-size: clamp(1.35rem, 3.6vw, 1.6rem);
          font-weight: 500;
          letter-spacing: -0.01em;
          line-height: 1.25;
          color: var(--ink);
          margin: 48px 0 12px;
          scroll-margin-top: 24px;
        }
        .legal-texto p { margin: 0 0 16px; }
        .legal-texto strong { font-weight: 600; color: var(--ink); }
        .legal-texto ul,
        .legal-texto ol { margin: 0 0 16px; padding-left: 22px; }
        .legal-texto ul { list-style: disc; }
        .legal-texto ol { list-style: decimal; }
        .legal-texto li { margin-bottom: 8px; }
        .legal-texto li::marker { color: var(--ink-mute); }

        .legal a { text-decoration: none; }
        .legal-cuerpo a {
          color: var(--ink);
          text-decoration: underline;
          text-decoration-color: var(--line-strong);
          text-underline-offset: 3px;
          transition: text-decoration-color 0.2s ease;
        }
        .legal-cuerpo a:hover { text-decoration-color: var(--ink); }

        .legal-texto blockquote {
          margin: 0 0 16px;
          padding: 16px 20px;
          border-left: 3px solid var(--ink);
          background: rgba(255, 255, 255, 0.5);
          border-radius: 0 12px 12px 0;
        }
        .legal-texto blockquote p:last-child { margin-bottom: 0; }

        .legal-tabla {
          overflow-x: auto;
          margin: 0 0 20px;
          border: 1px solid var(--line);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.5);
        }
        .legal-tabla table {
          width: 100%;
          min-width: 520px;
          border-collapse: collapse;
          font-size: 15px;
          line-height: 1.5;
        }
        .legal-tabla th,
        .legal-tabla td {
          text-align: left;
          vertical-align: top;
          padding: 12px 16px;
          border-bottom: 1px solid var(--line);
        }
        .legal-tabla th {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--ink-dim);
        }
        .legal-tabla tr:last-child td { border-bottom: none; }

        .legal-indice {
          margin: 32px 0 0;
          padding: 20px 24px;
          border: 1px solid var(--line);
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.5);
          font-size: 15px;
          line-height: 1.5;
        }
        .legal-indice-titulo {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--ink-dim);
          margin-bottom: 12px;
        }
        .legal-indice ul {
          list-style: none;
          margin: 0;
          padding: 0;
          columns: 2;
          column-gap: 28px;
        }
        .legal-indice li { margin: 0 0 8px; break-inside: avoid; }
        .legal-cuerpo .legal-indice a { text-decoration: none; }
        .legal-cuerpo .legal-indice a:hover { text-decoration: underline; }

        .legal-otra {
          margin: 48px 0 0;
          padding-top: 24px;
          border-top: 1px solid var(--line);
          font-size: 15px;
          color: var(--ink-dim);
        }

        @media (max-width: 560px) {
          .legal-cuerpo { font-size: 16px; padding: 32px 20px 56px; }
          .legal-barra { padding: 20px 20px 0; }
          .legal-indice ul { columns: 1; }
        }
      `}</style>
    </div>
  );
}
